import React, { useRef, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Box, Grid2 as Grid, Card, Typography, Avatar, Container, Stack, Skeleton, SxProps } from '@mui/material';
import useResponsive from 'hooks/useResponsive';
import type { ShowcaseCardItem, ShowcaseLinkItem, ShowcaseResponse } from '@creoate/virgo-content-api';
import CustomImage from 'components/CustomImage';

// --- Types ---
interface ShowcaseSectionProps {
  showcaseData: ShowcaseResponse;
  sx?: {};
}

interface ShowcaseSidebarProps {
  sectionTitle: string;
  showcaseLink: ShowcaseLinkItem[];
  loading?: boolean;
}

interface ShowcaseSidebarItemProps {
  loading: boolean;
  image?: string;
  label?: string;
  url?: string;
}

interface ShowcaseCardProps {
  loading: boolean;
  image?: string;
  title?: string;
  subtitle?: string;
  cardWidth?: number;
  sx?: SxProps;
}

interface ShowcaseCardsProps {
  showcaseCard: ShowcaseCardItem[];
  loading?: boolean;
  isMobile?: boolean;
}

// --- Style Constants ---
const styles = {
  sidebar: {
    container: {
      backgroundColor: '#fff',
      borderRadius: '10.87px',
      p: 1,
      border: { xs: '0.5px solid #e0e0e0', sm: '0.73px solid #e0e0e0' },
      height: '100%',
      mx: { xs: 1, sm: 0 },
    },
    link: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      textDecoration: 'none',
      color: 'inherit',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: 'bold',
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      textOverflow: 'ellipsis',
      WebkitBoxOrient: 'vertical',
      maxWidth: '100%',
    },
  },
  card: {
    base: {
      borderRadius: { xs: '7.65px', sm: '10.87px' },
      border: { xs: '0.52px solid #e0e0e0', sm: '0.73px solid #e0e0e0' },
      boxShadow: 'unset',
      p: 0.75,
      height: '100%',
      display: 'inline-flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '100%',
    },
    imageBox: {
      position: 'relative',
      width: '100%',
      aspectRatio: '1',
      overflow: 'hidden',
      borderRadius: { xs: '5.1px', sm: '7.25px' },
    },
    title: {
      fontSize: { xs: '0.75rem', lg: '0.95rem' },
      lineHeight: '140%',
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 1,
      WebkitBoxOrient: 'vertical',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
    },
  },
};

// --- Components ---
const ShowcaseSidebarItem = ({ loading, image, label, url }: ShowcaseSidebarItemProps) =>
  loading ? (
    <Box sx={styles.sidebar.link} data-testid="showcase-sidebar-link">
      <Skeleton variant="circular" width={45} height={45} />
      <Skeleton variant="text" width={70} height={20} />
    </Box>
  ) : (
    <a href={url} style={{ textDecoration: 'none', color: 'inherit' }} target="_blank" rel="noopener noreferrer">
      <Box sx={styles.sidebar.link} data-testid="showcase-sidebar-link">
        <Avatar
          src={image}
          sx={{ width: 45, height: 45 }}
          slotProps={{ img: { 'data-testid': `showcase-sidebar-avatar-img-${label}` } as any }}
        />
        <Typography variant="body2" sx={styles.sidebar.label}>
          {label}
        </Typography>
      </Box>
    </a>
  );

const ShowcaseSidebar = ({ sectionTitle, showcaseLink, loading = false }: ShowcaseSidebarProps): JSX.Element => (
  <Stack sx={styles.sidebar.container}>
    {loading ? (
      <Skeleton variant="text" width={120} height={28} sx={{ mx: 'auto', mb: 1 }} />
    ) : (
      <Typography variant="body1" fontWeight="bold" color="#497262" textAlign="center" mb={1}>
        {sectionTitle}
      </Typography>
    )}
    <Box
      sx={{
        display: { xs: 'grid', md: 'flex' },
        gridTemplateColumns: { xs: '1fr 1fr' },
        gap: 1,
        flexDirection: { md: 'column' },
        justifyContent: { md: 'space-between' },
        flex: 1,
      }}
    >
      {(showcaseLink || []).slice(0, 8).map((item, i) => (
        <ShowcaseSidebarItem
          key={item?.id ?? i}
          loading={loading}
          image={item?.image?.url}
          label={item?.label}
          url={item?.url}
        />
      ))}
    </Box>
  </Stack>
);

const ShowcaseCard = ({ loading, image, title, subtitle, sx, cardWidth }: ShowcaseCardProps) => {
  return (
    <Card sx={{ ...sx, width: { xs: cardWidth || 'inherit', sm: (sx as any)?.width } }}>
      <Box sx={styles.card.imageBox}>
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : (
          <CustomImage
            src={image!}
            alt={title!}
            fill
            style={{ objectFit: 'cover' }}
            unoptimized
            data-testid={`showcase-card-image-${title}`}
          />
        )}
      </Box>
      <Box sx={{ pt: { xs: 0.5, sm: 0.25 } }}>
        {loading ? (
          <>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="60%" height={18} />
          </>
        ) : (
          <>
            <Typography
              variant="body1"
              sx={styles.card.title}
              data-testid="showcase-card-title"
              dangerouslySetInnerHTML={{ __html: title || '' }}
            />
            <Typography
              variant="body2"
              sx={styles.card.title}
              data-testid="showcase-card-subtitle"
              dangerouslySetInnerHTML={{ __html: subtitle || '' }}
            />
          </>
        )}
      </Box>
    </Card>
  );
};

const ShowcaseCards = ({ showcaseCard, loading = false, isMobile }: ShowcaseCardsProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => containerRef.current && setContainerWidth(containerRef.current.offsetWidth);
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [loading]);

  const cardWidth = isMobile && containerWidth ? containerWidth / 2.6 : undefined;

  const renderCard = (item: ShowcaseCardItem) => (
    <a
      key={item.id}
      href={item?.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}
    >
      <ShowcaseCard
        loading={loading}
        image={item?.image?.url}
        title={item?.title}
        subtitle={item?.subtitle}
        cardWidth={cardWidth}
        sx={styles.card.base}
      />
    </a>
  );

  const limitedShowcaseCard = (showcaseCard || []).slice(0, 8);

  if (loading) {
    const placeholders = Array.from({ length: 8 }).map((_, i) => (
      <ShowcaseCard
        // eslint-disable-next-line react/no-array-index-key
        key={`placeholder-${i}`}
        loading
        sx={{
          ...styles.card.base,
          display: { xs: i > 3 ? 'none' : styles.card.base.display, sm: styles.card.base.display },
        }}
      />
    ));
    return (
      <Box sx={{ width: '100%', height: '100%', px: { xs: 1, sm: 0 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
            rowGap: { xs: 0.75, sm: 0.5, lg: 0.75 },
            columnGap: { xs: 0.5, sm: 0.5, lg: 0.75 },
            height: { lg: '100%' },
          }}
        >
          {placeholders}
        </Box>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          px: 1,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gridTemplateRows: 'repeat(2, 1fr)',
            columnGap: 0.5,
            rowGap: 0.75,
            width: 'max-content',
          }}
        >
          {limitedShowcaseCard.map(renderCard)}
        </Box>
      </Box>
    );
  }
  return (
    <Grid container sx={{ height: { lg: '100%' } }} spacing={{ sm: 0.5, lg: 0.75 }}>
      {limitedShowcaseCard.map((item) => (
        <Grid size={3} key={item.id} sx={{ display: 'inline-flex' }}>
          {renderCard(item)}
        </Grid>
      ))}
    </Grid>
  );
};

const ShowcaseSection = ({ showcaseData, sx }: ShowcaseSectionProps): JSX.Element | null => {
  const isMobile = useResponsive('down', 'sm');
  const [loading, setLoading] = useState(true);

  useEffect(() => setLoading(false), []);

  if (!showcaseData || (!showcaseData.showcaseLink?.length && !showcaseData.showcaseCard?.length)) return null;
  return (
    <Box sx={{ backgroundColor: '#f8fafa', mb: 1, ...sx }} data-testid="showcase-section">
      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 3 } }}>
        <Grid container columns={16} alignItems="stretch" spacing={0.75} py={1}>
          {showcaseData.showcaseLink.length > 0 && (
            <Grid sx={{ order: { xs: 2, md: 1 } }} size={{ xs: 16, md: 4, lg: 3 }}>
              <ShowcaseSidebar
                sectionTitle={showcaseData?.sectionTitle}
                showcaseLink={showcaseData?.showcaseLink}
                loading={loading}
              />
            </Grid>
          )}
          {showcaseData.showcaseCard.length > 0 && (
            <Grid sx={{ order: { xs: 1, md: 2 } }} size={{ xs: 16, md: 12, lg: 13 }}>
              <ShowcaseCards showcaseCard={showcaseData?.showcaseCard} loading={loading} isMobile={isMobile} />
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default ShowcaseSection;
